"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Tabs as TabsPrimitive } from "radix-ui"

import { cn } from "../../lib/utils"
import { triggerHaptic } from "../../lib/haptics"

function Tabs({
  className,
  orientation = "horizontal",
  onValueChange,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-horizontal:flex-col",
        className
      )}
      onValueChange={(value) => {
        triggerHaptic("selection")
        onValueChange?.(value)
      }}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  "rounded-full p-[3px] group-data-horizontal/tabs:h-control group/tabs-list text-muted-foreground inline-flex w-fit items-center justify-center group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col",
  {
    variants: {
      variant: {
        default: "bg-muted",
        line:
          // Scrolls horizontally (no clipped tabs) on narrow screens; scrollbar hidden for the native feel.
          "relative h-11 w-full items-end justify-start gap-8 rounded-none border-0 border-b border-border bg-transparent p-0 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      },
    },
    defaultVariants: {
      variant: "line",
    },
  }
)

function TabsList({
  children,
  className,
  variant = "line",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  const listRef = React.useRef<HTMLDivElement | null>(null)
  const [indicatorStyle, setIndicatorStyle] = React.useState<{
    width: number
    x: number
    ready: boolean
  }>({
    width: 0,
    x: 0,
    ready: false,
  })

  const updateIndicator = React.useCallback(() => {
    if (variant !== "line") return

    const list = listRef.current
    if (!list) return

    const activeTrigger = list.querySelector<HTMLElement>('[data-slot="tabs-trigger"][data-state="active"]')
    if (!activeTrigger) {
      return
    }

    const nextWidth = activeTrigger.offsetWidth
    const nextX = activeTrigger.offsetLeft

    setIndicatorStyle((current) => {
      if (current.ready && current.width === nextWidth && current.x === nextX) {
        return current
      }

      return {
        width: nextWidth,
        x: nextX,
        ready: true,
      }
    })
  }, [variant])

  React.useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(updateIndicator)
    return () => window.cancelAnimationFrame(frame)
  }, [children, updateIndicator])

  React.useEffect(() => {
    if (variant !== "line") return

    const list = listRef.current
    if (!list) return

    if (typeof ResizeObserver === "undefined") {
      return
    }

    const observer = new ResizeObserver(() => {
      updateIndicator()
    })

    observer.observe(list)

    const activeTrigger = list.querySelector<HTMLElement>('[data-slot="tabs-trigger"][data-state="active"]')
    if (activeTrigger) {
      observer.observe(activeTrigger)
    }

    return () => observer.disconnect()
  }, [children, updateIndicator, variant])

  React.useEffect(() => {
    if (variant !== "line") return

    const list = listRef.current
    if (!list || typeof MutationObserver === "undefined") return

    const observer = new MutationObserver(() => {
      updateIndicator()
    })

    observer.observe(list, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
    })

    return () => observer.disconnect()
  }, [updateIndicator, variant])

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    >
      {children}
      {variant === "line" ? (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute bottom-0 left-0 h-0.5 rounded-full bg-foreground",
            indicatorStyle.ready ? "opacity-100" : "opacity-0",
          )}
          style={{
            width: `${indicatorStyle.width}px`,
            transform: `translateX(${indicatorStyle.x}px)`,
          }}
        />
      ) : null}
    </TabsPrimitive.List>
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "h-[calc(100%-1px)] flex-1 rounded-full border border-transparent px-1.5 py-0.5 text-sm font-medium text-foreground/60 hover:text-foreground group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start group-data-[variant=default]/tabs-list:data-[state=active]:bg-background group-data-[variant=default]/tabs-list:data-[state=active]:text-foreground group-data-[variant=default]/tabs-list:data-[state=active]:shadow-sm",
        "group-data-[variant=line]/tabs-list:h-full group-data-[variant=line]/tabs-list:self-end group-data-[variant=line]/tabs-list:flex-none group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:border-0 group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:px-0 group-data-[variant=line]/tabs-list:pt-0 group-data-[variant=line]/tabs-list:pb-2 group-data-[variant=line]/tabs-list:text-sm group-data-[variant=line]/tabs-list:font-medium group-data-[variant=line]/tabs-list:text-foreground/50 group-data-[variant=line]/tabs-list:hover:text-foreground/75 group-data-[variant=line]/tabs-list:data-[state=active]:text-foreground",
        className
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 text-sm outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
